// Order Rush — the restaurant's dinner-dash, ported from the v6 kitchen
// prototype into a streamlined tap overlay. Customers sit down and order toast
// with specific toppings; build a toast to match a ticket and serve it before
// their patience runs out. Combos build; walkouts and wrong toppings hurt.
// This module owns the overlay + scoring; the restaurant scene reacts via
// onServe (e.g. the chef flips a fresh toast).

const DURATION = 60;
const MAX_TICKETS = 4;

// toppings the kitchen stocks (colors mirror the prototype)
export const TOPPINGS = {
  pb:     { css: '#C68B4F', label: 'PB' },
  jelly:  { css: '#E6537B', label: 'jelly' },
  honey:  { css: '#F49B33', label: 'honey' },
  butter: { css: '#FFE08A', label: 'butter' }
};
const KEYS = Object.keys(TOPPINGS);
const WHO = ['#E6537B', '#3D5FC0', '#7FBF6A', '#F49B33', '#8FD14F', '#BEE3EF'];

const sortKey = (arr) => arr.slice().sort().join('+');

export class OrderRush {
  constructor({ onServe, onFinish } = {}){
    this.onServe = onServe || (() => {});
    this.onFinish = onFinish || (() => {});
    this.active = false;
    this.finished = false;

    this.timeLeft = DURATION;
    this.spawnT = 1.5;
    this.served = 0; this.walkouts = 0; this.combo = 0; this.bestCombo = 0; this.points = 0;
    this.tickets = [];        // { tops, key, color, t, max, el, fill }
    this.tray = [];           // toppings on the toast in hand

    this._onKey = this._onKey.bind(this);
  }

  mount(root){
    const el = document.createElement('div');
    el.className = 'rush-hud';
    el.innerHTML = `
      <div class="rush-info">
        <span class="pill rush-timer">1:00</span>
        <span class="pill rush-served">0 served</span>
        <span class="pill rush-combo" hidden></span>
      </div>
      <div class="rush-tickets"></div>
      <div class="rush-feedback"></div>
      <div class="rush-tray"><span class="rush-tray-label">your toast:</span><span class="rush-tray-chips"></span></div>
      <div class="rush-toppings">
        ${KEYS.map(k => `<button class="rush-top" data-top="${k}" style="--c:${TOPPINGS[k].css}"><span></span>${TOPPINGS[k].label}</button>`).join('')}
        <button class="rush-toss">🗑 toss</button>
      </div>`;
    (root || document.body).appendChild(el);
    this.el = el;
    this.timerEl = el.querySelector('.rush-timer');
    this.servedEl = el.querySelector('.rush-served');
    this.comboEl = el.querySelector('.rush-combo');
    this.ticketsEl = el.querySelector('.rush-tickets');
    this.feedbackEl = el.querySelector('.rush-feedback');
    this.trayChipsEl = el.querySelector('.rush-tray-chips');

    el.querySelectorAll('.rush-top').forEach(b =>
      b.addEventListener('pointerdown', e => { e.preventDefault(); this._addTopping(b.dataset.top); }));
    el.querySelector('.rush-toss').addEventListener('pointerdown', e => { e.preventDefault(); this._toss(); });
    window.addEventListener('keydown', this._onKey);
    this._renderTray();
  }

  begin(){ this.active = true; this._spawn(); }

  _onKey(e){
    if (!this.active) return;
    const n = parseInt(e.key, 10);
    if (n >= 1 && n <= KEYS.length) this._addTopping(KEYS[n - 1]);
    else if (e.key === 'Backspace' || e.key === '0'){ e.preventDefault(); this._toss(); }
    else if (e.key === ' ' || e.key === 'Enter'){ e.preventDefault(); this._autoServe(); }
  }

  _addTopping(top){
    if (!this.active) return;
    if (this.tray.length >= 2){ this._feedback('two toppings max!', 'bad'); return; }
    if (this.tray.includes(top)){ this._feedback('already on there', 'bad'); return; }
    this.tray.push(top);
    this._renderTray();
    // if the toast now matches a waiting ticket exactly, auto-serve it
    this._autoServe(true);
  }

  _toss(){ this.tray = []; this._renderTray(); }

  // serve the current toast to a matching ticket (soft = only if a match exists)
  _autoServe(soft){
    if (!this.tray.length) { if (!soft) this._feedback('add toppings first', 'bad'); return; }
    const key = sortKey(this.tray);
    const idx = this.tickets.findIndex(t => t.key === key);
    if (idx === -1){ if (!soft) this._feedback('no one ordered that', 'bad'); return; }
    this._serve(idx);
  }

  _serveTo(ticketIdx){
    if (!this.tray.length){ this._feedback('build a toast first!', 'bad'); return; }
    const t = this.tickets[ticketIdx];
    if (sortKey(this.tray) === t.key) this._serve(ticketIdx);
    else this._feedback("that's not their order", 'bad');
  }

  _serve(idx){
    const t = this.tickets[idx];
    this.served++; this.combo++; this.bestCombo = Math.max(this.bestCombo, this.combo);
    this.points += 5 + (t.tops.length - 1) * 4 + Math.min(this.combo, 10);
    t.el.remove(); this.tickets.splice(idx, 1);
    this.tray = [];
    this._renderTray(); this._renderScore();
    this._feedback(this.combo >= 3 ? `served! 🔥${this.combo}` : 'served! ✓', 'good');
    this.onServe({ combo: this.combo });
  }

  _spawn(){
    if (this.tickets.length >= MAX_TICKETS) return;
    const n = Math.random() < 0.5 ? 1 : 2;
    const tops = [];
    while (tops.length < n){
      const k = KEYS[(Math.random() * KEYS.length) | 0];
      if (!tops.includes(k)) tops.push(k);
    }
    const patience = (n > 1 ? 20 : 15) + Math.random() * 4;
    const color = WHO[(Math.random() * WHO.length) | 0];
    const el = document.createElement('button');
    el.className = 'rush-ticket in';
    el.innerHTML = `<span class="who" style="background:${color}"></span>
      <span class="rush-chips">${tops.map(t => `<span class="chip" style="background:${TOPPINGS[t].css}"></span>`).join('')}</span>
      <span class="obar"><i></i></span>`;
    const t = { tops, key: sortKey(tops), color, t: patience, max: patience, el, fill: el.querySelector('.obar > i') };
    el.addEventListener('pointerdown', e => { e.preventDefault(); this._serveTo(this.tickets.indexOf(t)); });
    this.ticketsEl.appendChild(el);
    this.tickets.push(t);
  }

  update(dt){
    if (!this.active || this.finished) return;

    this.timeLeft -= dt;
    if (this.timeLeft <= 0){ this.timeLeft = 0; this._renderTimer(); return this._finish(); }
    this._renderTimer();

    this.spawnT -= dt;
    if (this.spawnT <= 0){ this.spawnT = 4.5 + Math.random() * 2.5; this._spawn(); }

    for (let i = this.tickets.length - 1; i >= 0; i--){
      const t = this.tickets[i];
      t.t -= dt;
      t.fill.style.width = `${Math.max(0, t.t / t.max * 100)}%`;
      if (t.t <= 0){                       // customer walked out
        t.el.remove(); this.tickets.splice(i, 1);
        this.walkouts++; this.combo = 0;
        this._feedback('walked out!', 'bad');
        this._renderScore();
      }
    }
  }

  _renderTray(){
    this.trayChipsEl.innerHTML = this.tray.length
      ? this.tray.map(t => `<span class="chip" style="background:${TOPPINGS[t].css}"></span>`).join('')
      : '<i class="rush-empty">empty</i>';
  }

  _renderTimer(){
    const s = Math.ceil(this.timeLeft);
    this.timerEl.textContent = `0:${String(s).padStart(2, '0')}`;
    this.timerEl.classList.toggle('low', s <= 8);
  }

  _renderScore(){
    this.servedEl.textContent = `${this.served} served`;
    if (this.combo >= 2){
      this.comboEl.hidden = false;
      this.comboEl.textContent = `🔥 ${this.combo} combo`;
      this.comboEl.classList.remove('pulse'); void this.comboEl.offsetWidth; this.comboEl.classList.add('pulse');
    } else this.comboEl.hidden = true;
  }

  _feedback(text, cls){
    if (!this.feedbackEl) return;
    this.feedbackEl.textContent = text;
    this.feedbackEl.className = `rush-feedback show ${cls}`;
    void this.feedbackEl.offsetWidth; this.feedbackEl.classList.add('anim');
  }

  _finish(){
    if (this.finished) return;
    this.finished = true; this.active = false;
    const total = this.served + this.walkouts;
    const rating = total ? Math.round(this.served / total * 100) : 0;
    const crumbs = this.served + Math.floor(this.bestCombo / 3);
    this.onFinish({ served: this.served, walkouts: this.walkouts, bestCombo: this.bestCombo, points: this.points, rating, crumbs });
  }

  unmount(){
    window.removeEventListener('keydown', this._onKey);
    if (this.el) this.el.remove();
    this.el = null;
  }
}
